import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Target, ArrowLeft, Clock, MapPin, Trophy, Calendar, RefreshCw, Crown } from "lucide-react";

const API = '/api';

export default function ShootOffBoard() {
  const { eventId } = useParams();
  const { isAdmin, getAuthHeader } = useAuth();
  const [event, setEvent] = useState(null);
  const [matches, setMatches] = useState([]);
  const [shootoffs, setShootoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Schedule dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedShootoff, setSelectedShootoff] = useState(null);
  const [scheduleData, setScheduleData] = useState({
    scheduled_datetime: "",
    trap_range: "",
  });

  useEffect(() => {
    fetchData();
  }, [eventId]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, eventId]);

  const fetchData = async () => {
    try {
      const [eventRes, matchesRes, shootoffsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/matches?event_id=${eventId}`),
        axios.get(`${API}/shootoffs?event_id=${eventId}`),
      ]);
      setEvent(eventRes.data);
      setMatches(matchesRes.data);
      setShootoffs(shootoffsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const getMatchName = (matchId) => {
    const match = matches.find(m => m.id === matchId);
    return match?.name || "Unknown Match";
  };

  const openScheduleDialog = (shootoff) => {
    setSelectedShootoff(shootoff);
    setScheduleData({
      scheduled_datetime: shootoff.scheduled_datetime || "",
      trap_range: shootoff.trap_range || "",
    });
    setScheduleDialogOpen(true);
  };

  const handleSchedule = async () => {
    if (!selectedShootoff || !scheduleData.scheduled_datetime || !scheduleData.trap_range) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.put(`${API}/shootoffs/${selectedShootoff.id}/schedule`, scheduleData, {
        headers: getAuthHeader()
      });
      toast.success("Shoot-off scheduled");
      setScheduleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to schedule shoot-off");
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return null;
    try {
      return new Date(dt).toLocaleString();
    } catch {
      return dt;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800">Pending Schedule</Badge>;
      case "scheduled":
        return <Badge className="bg-blue-100 text-blue-800"><Clock size={12} className="mr-1" /> Scheduled</Badge>;
      case "completed":
        return <Badge className="bg-green-100 text-green-800"><Trophy size={12} className="mr-1" /> Completed</Badge>;
      default:
        return null;
    }
  };

  const pendingShootoffs = shootoffs.filter(s => s.status === "pending");
  const scheduledShootoffs = shootoffs.filter(s => s.status === "scheduled");
  const completedShootoffs = shootoffs.filter(s => s.status === "completed");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="shootoff-board-page" className="space-y-6">
      {/* Header */}
      <div>
        <Link to={`/matches/${eventId}`} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2">
          <ArrowLeft size={14} /> Back to Matches
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
              Shoot-Off Board
            </h1>
            <p className="text-slate-500 mt-1">{event?.name}</p>
          </div>

          <div className="flex items-center gap-2">
            <RefreshCw size={16} className={autoRefresh ? "animate-spin text-green-600" : "text-slate-400"} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? "Pause" : "Resume"} Auto-Refresh
            </Button>
          </div>
        </div>
      </div>

      {shootoffs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No shoot-offs scheduled yet.</p>
            <p className="text-sm text-slate-400 mt-1">Shoot-offs will appear here when ties are detected.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Scheduled Shoot-offs - Most Important */}
          {scheduledShootoffs.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Clock className="text-blue-600" /> Upcoming Shoot-Offs
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {scheduledShootoffs.map((shootoff) => (
                  <Card key={shootoff.id} className="border-2 border-blue-300 bg-blue-50/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-amber-100 text-amber-800 text-lg px-3 py-1">
                          <Crown size={16} className="mr-1" /> {shootoff.position} Place
                        </Badge>
                        {getStatusBadge(shootoff.status)}
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-2">{getMatchName(shootoff.match_id)}</p>
                      
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <p className="font-mono text-xl font-bold text-center mb-2">
                          {shootoff.tied_shooter_names.join(" vs ")}
                        </p>
                        <p className="text-center text-slate-500">
                          Tied at <span className="font-mono font-bold text-green-700">{shootoff.tied_score}</span> points
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-blue-100 rounded-lg p-3">
                          <Calendar className="mx-auto text-blue-600 mb-1" size={20} />
                          <p className="text-xs text-slate-500">When</p>
                          <p className="font-bold text-sm">{formatDateTime(shootoff.scheduled_datetime)}</p>
                        </div>
                        <div className="bg-blue-100 rounded-lg p-3">
                          <MapPin className="mx-auto text-blue-600 mb-1" size={20} />
                          <p className="text-xs text-slate-500">Where</p>
                          <p className="font-bold text-sm">{shootoff.trap_range}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Pending Shoot-offs */}
          {pendingShootoffs.length > 0 && (
            <div>
              <h2 className="font-serif text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Target className="text-yellow-600" /> Awaiting Schedule ({pendingShootoffs.length})
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {pendingShootoffs.map((shootoff) => (
                  <Card key={shootoff.id} className="border-yellow-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className="bg-amber-100 text-amber-800">
                          {shootoff.position} Place
                        </Badge>
                        {getStatusBadge(shootoff.status)}
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-2">{getMatchName(shootoff.match_id)}</p>
                      <p className="font-medium">{shootoff.tied_shooter_names.join(" vs ")}</p>
                      <p className="text-sm text-slate-500">Tied at {shootoff.tied_score} points</p>

                      {isAdmin() && (
                        <Button
                          size="sm"
                          className="mt-3 w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                          onClick={() => openScheduleDialog(shootoff)}
                        >
                          <Calendar size={14} className="mr-1" /> Schedule Shoot-Off
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Completed Shoot-offs */}
          {completedShootoffs.length > 0 && (
            <div>
              <h2 className="font-serif text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Trophy className="text-green-600" /> Completed ({completedShootoffs.length})
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedShootoffs.map((shootoff) => (
                  <Card key={shootoff.id} className="bg-green-50/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className="bg-amber-100 text-amber-800">
                          {shootoff.position} Place
                        </Badge>
                        {getStatusBadge(shootoff.status)}
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-2">{getMatchName(shootoff.match_id)}</p>
                      
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Winner</p>
                        <p className="font-bold text-lg text-green-700">{shootoff.winner_name}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Schedule Shoot-Off</DialogTitle>
            <DialogDescription>
              Set the time and location for this shoot-off
            </DialogDescription>
          </DialogHeader>
          
          {selectedShootoff && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium">{selectedShootoff.position} Place - {getMatchName(selectedShootoff.match_id)}</p>
                <p className="text-sm text-slate-600">{selectedShootoff.tied_shooter_names.join(" vs ")}</p>
              </div>

              <div>
                <Label htmlFor="schedule-dt">Date & Time</Label>
                <Input
                  id="schedule-dt"
                  type="datetime-local"
                  value={scheduleData.scheduled_datetime}
                  onChange={(e) => setScheduleData({ ...scheduleData, scheduled_datetime: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="schedule-range">Trap Range</Label>
                <Input
                  id="schedule-range"
                  value={scheduleData.trap_range}
                  onChange={(e) => setScheduleData({ ...scheduleData, trap_range: e.target.value })}
                  placeholder="e.g., Range 1, Range 3"
                  required
                />
              </div>

              <Button
                onClick={handleSchedule}
                className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
              >
                Schedule Shoot-Off
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
